/**
 * BenchmarkChart Component
 *
 * Displays facility benchmarking data with bar charts
 * and AI-generated insights.
 */

import React from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Paper,
  Stack,
  Divider,
  useTheme,
  LinearProgress
} from '@mui/material';
import {
  TrendingUp as UpIcon,
  TrendingDown as DownIcon,
  TrendingFlat as FlatIcon,
  EmojiEvents as TrophyIcon,
  Lightbulb as InsightIcon
} from '@mui/icons-material';
import {
  BenchmarkResponse,
  FacilityBenchmark,
  BenchmarkInsight
} from '../../services/insightService';

interface BenchmarkChartProps {
  data: BenchmarkResponse;
}

const formatNumber = (value: number | undefined, decimals: number = 2): string => {
  if (value === undefined || value === null) return 'N/A';
  return value.toLocaleString(undefined, {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals
  });
};

const getPerformanceIcon = (perf: number) => {
  if (perf < -5) return <DownIcon sx={{ color: 'success.main' }} />;
  if (perf > 5) return <UpIcon sx={{ color: 'error.main' }} />;
  return <FlatIcon sx={{ color: 'warning.main' }} />;
};

const getPerformanceLabel = (perf: number): string => {
  if (perf < -10) return 'Excellent';
  if (perf < 0) return 'Above Avg';
  if (perf === 0) return 'Average';
  if (perf < 10) return 'Below Avg';
  return 'Needs Work';
};

const BenchmarkChart: React.FC<BenchmarkChartProps> = ({ data }) => {
  const theme = useTheme();

  // Find max emissions for relative bar widths
  const maxEmissions = Math.max(...data.facilities.map(f => f.total_emissions), 1);

  return (
    <Box>
      {/* Summary Stats */}
      <Stack direction="row" spacing={3} mb={3}>
        <Card sx={{ flex: 1 }}>
          <CardContent>
            <Typography variant="caption" color="text.secondary">
              Total Org Emissions
            </Typography>
            <Typography variant="h5" fontWeight={700}>
              {formatNumber(data.total_org_emissions, 1)} tCO2e
            </Typography>
          </CardContent>
        </Card>

        {data.org_average_intensity_sqm && (
          <Card sx={{ flex: 1 }}>
            <CardContent>
              <Typography variant="caption" color="text.secondary">
                Avg Intensity / m²
              </Typography>
              <Typography variant="h5" fontWeight={700}>
                {formatNumber(data.org_average_intensity_sqm, 4)} tCO2e
              </Typography>
            </CardContent>
          </Card>
        )}

        {data.org_average_intensity_employee && (
          <Card sx={{ flex: 1 }}>
            <CardContent>
              <Typography variant="caption" color="text.secondary">
                Avg Intensity / Employee
              </Typography>
              <Typography variant="h5" fontWeight={700}>
                {formatNumber(data.org_average_intensity_employee, 2)} tCO2e
              </Typography>
            </CardContent>
          </Card>
        )}
      </Stack>

      {/* Facility Comparison Table */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Facility Comparison
          </Typography>

          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Rank</TableCell>
                  <TableCell>Facility</TableCell>
                  <TableCell align="right">Emissions</TableCell>
                  <TableCell sx={{ width: '20%' }}>Distribution</TableCell>
                  <TableCell align="right">Intensity/m²</TableCell>
                  <TableCell align="right">Intensity/Employee</TableCell>
                  <TableCell align="center">vs Avg</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data.facilities.map((facility, index) => (
                  <TableRow
                    key={facility.facility_id}
                    sx={{
                      bgcolor: index === 0
                        ? `${theme.palette.success.main}10`
                        : 'inherit'
                    }}
                  >
                    <TableCell>
                      <Box display="flex" alignItems="center" gap={1}>
                        {index === 0 && (
                          <TrophyIcon
                            sx={{ color: 'warning.main', fontSize: 18 }}
                          />
                        )}
                        <Typography fontWeight={index === 0 ? 700 : 400}>
                          #{facility.rank_in_org}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography fontWeight={500}>
                        {facility.facility_name}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography fontWeight={600}>
                        {formatNumber(facility.total_emissions, 1)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        tCO2e
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <LinearProgress
                        variant="determinate"
                        value={(facility.total_emissions / maxEmissions) * 100}
                        sx={{
                          height: 8,
                          borderRadius: 4,
                          bgcolor: theme.palette.grey[200],
                          '& .MuiLinearProgress-bar': {
                            borderRadius: 4,
                            bgcolor: theme.palette.primary.main
                          }
                        }}
                      />
                    </TableCell>
                    <TableCell align="right">
                      {facility.intensity_per_sqm
                        ? formatNumber(facility.intensity_per_sqm, 4)
                        : 'N/A'}
                    </TableCell>
                    <TableCell align="right">
                      {facility.intensity_per_employee
                        ? formatNumber(facility.intensity_per_employee, 2)
                        : 'N/A'}
                    </TableCell>
                    <TableCell align="center">
                      <Box display="flex" alignItems="center" justifyContent="center" gap={0.5}>
                        {getPerformanceIcon(facility.performance_vs_avg)}
                        <Chip
                          label={getPerformanceLabel(facility.performance_vs_avg)}
                          size="small"
                          color={
                            facility.performance_vs_avg < 0
                              ? 'success'
                              : facility.performance_vs_avg > 10
                              ? 'error'
                              : 'default'
                          }
                          variant="outlined"
                          sx={{ fontSize: '0.7rem' }}
                        />
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* AI Insights */}
      {data.insights.length > 0 && (
        <Card>
          <CardContent>
            <Box display="flex" alignItems="center" gap={1} mb={2}>
              <InsightIcon color="primary" />
              <Typography variant="h6">AI Benchmark Insights</Typography>
            </Box>

            <Stack spacing={2}>
              {data.insights.map((insight) => (
                <Paper
                  key={insight.id}
                  variant="outlined"
                  sx={{ p: 2 }}
                >
                  <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                    {insight.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" paragraph>
                    {insight.description}
                  </Typography>

                  {(insight.best_performer || insight.worst_performer) && (
                    <Box display="flex" gap={2} mb={2}>
                      {insight.best_performer && (
                        <Chip
                          icon={<TrophyIcon />}
                          label={`Best: ${insight.best_performer}`}
                          color="success"
                          size="small"
                          variant="outlined"
                        />
                      )}
                      {insight.worst_performer && (
                        <Chip
                          label={`Needs Improvement: ${insight.worst_performer}`}
                          color="warning"
                          size="small"
                          variant="outlined"
                        />
                      )}
                    </Box>
                  )}

                  {insight.recommendations.length > 0 && (
                    <>
                      <Divider sx={{ my: 1 }} />
                      <Typography variant="caption" color="text.secondary" display="block" mb={1}>
                        Recommendations:
                      </Typography>
                      <Stack spacing={0.5}>
                        {insight.recommendations.map((rec, idx) => (
                          <Typography key={idx} variant="body2">
                            • {rec}
                          </Typography>
                        ))}
                      </Stack>
                    </>
                  )}
                </Paper>
              ))}
            </Stack>
          </CardContent>
        </Card>
      )}
    </Box>
  );
};

export default BenchmarkChart;
